import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { MessageConstants } from '../../../shared/constants/message-constants';
import { SkillConstants } from '../../constants/skill-constants';
import { ISkillSaveRequest } from '../../models/skill-save-request';
import { SkillPersistenceService } from '../../services/skill-persistence.service';
import { ValidityPeriod } from '../../../course/models/validityPeriod';
import { SkillDetail } from '../../models/skill-detail.model';

@Component({
  selector: 'app-skill-creation-slider',
  templateUrl: './skill-creation-slider.component.html',
  styleUrls: ['./skill-creation-slider.component.scss']
})
export class SkillCreationSliderComponent implements OnInit {
  @Input() isSkillSliderOpen: boolean = false;
  @Input() isEditMode: boolean = false;
  @Input() skillDetailsData: SkillDetail | null = null;
  @Output() isSkillSliderActive = new EventEmitter<boolean>();

  activeTab = 0;
  skillForm!: FormGroup;
  eQMSScopesList: { eQMSScopeId: number; eQMSScopeName: string }[] = [];
  valueField = 'eQMSScopeId';
  labelField = 'eQMSScopeName';
  isValidityPeriodRangeEntered = false;
  mandatoryFields = this.getDefaultMandatoryFieldsObject();
  errorMessages: string[] = [];
  errorHeaderMessage : string = SkillConstants.Skill_ErrorMessage_Header;
isActiveCheckBoxToolTip: string= SkillConstants.Skill_IsActive_Checkbox_Tooltip;
  constructor(
    private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private skillPersistenceService: SkillPersistenceService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initialize(); 
    this.errorHeaderMessage = this.isEditMode ? SkillConstants.Skill_Update_ErrorMessage_Header : SkillConstants.Skill_ErrorMessage_Header;
    this.skillForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      eqmsScopes: [[], [Validators.required]],
      description: [''],
      trainingCriteria: [''],
      validityPeriodRange: [null],
      validityPeriodInterval: [null],
      verificationRequired: [false],
      acknowledgementRequired: [false],
      isActive: [true],
    });
if(this.isSkillSliderOpen){
    this.skillPersistenceService
      .getScopes()
      .subscribe((scopes: any[]) => {
        this.eQMSScopesList = scopes.map((scope) => ({
          eQMSScopeId: scope.eQMSScopeId,
          eQMSScopeName: scope.eQMSScopeName
        }));
      });
    }

    if (this.isEditMode && this.skillDetailsData) {
      this.MapSkillsDataToSlider(this.skillDetailsData);
    }

    this.patchValueTovalidityPeriodInterval();
  }

  initialize(){
    this.skillPersistenceService.closeAndResetSkillSlider$.subscribe((shouldClose) => {
      if(shouldClose){
        this.closeSliderWithoutConfirmation();
      }
    });
  }

  patchValueTovalidityPeriodInterval() {
    this.skillForm
      .get('validityPeriodRange')
      ?.valueChanges.subscribe((value) => {
        const isRangeEntered =
          value !== null && value !== undefined && value !== '';
        this.isValidityPeriodRangeEntered = isRangeEntered;

        const validityPeriodIntervalControl = this.skillForm.get(
          'validityPeriodInterval'
        );
        if (!validityPeriodIntervalControl) {
          return;
        }

        if (isRangeEntered) {
          validityPeriodIntervalControl.setValidators([Validators.required]);
        } else {
          validityPeriodIntervalControl.clearValidators();
          validityPeriodIntervalControl.setValue(null, { emitEvent: false });
        }

        validityPeriodIntervalControl.updateValueAndValidity({
          emitEvent: false,
        });
      });
  }

  closeSlider() {
    this.confirmationService.confirm({
      message: MessageConstants.Slider_Confirmation_Text,
      header: MessageConstants.Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeSliderWithoutConfirmation();
      }
    });
  }

  private closeSliderWithoutConfirmation() {
    this.isSkillSliderOpen = false;
    this.skillForm.reset();
    this.activeTab = 0;
    this.isSkillSliderActive.emit(this.isSkillSliderOpen);
    this.errorMessages = [];
    this.mandatoryFields = this.getDefaultMandatoryFieldsObject();
  }

  saveSkill() {
    this.validateForm();
    this.validateValidityPeriod();
    if (this.errorMessages.length > 0) {
      return;
    }

    if (this.isEditMode) {
      this.UpdateSkill();
      return;
    }

    const formValue = this.skillForm.getRawValue();
    let validityPeriod: ValidityPeriod={
        range: formValue.validityPeriodRange === ''? null : formValue.validityPeriodRange,
        interval: formValue.validityPeriodInterval
      };
    const skillPayload: ISkillSaveRequest = {
      code: '',
      name: formValue.name || '',
      eQMSScopes: (formValue.eqmsScopes || []).map((scopeId: number | string) => scopeId.toString()),
      description: formValue.description || '',
      trainingCriteria: formValue.trainingCriteria || '',
      validityPeriod: validityPeriod,
      verificationRequired: !!formValue.verificationRequired,
      acknowledgementRequired: !!formValue.acknowledgementRequired,
      isActive: !!formValue.isActive

    };

    this.skillPersistenceService.saveSkill(skillPayload);
  }

  /**
   * Maps the skill details data from API to slider form controls.
   */
  MapSkillsDataToSlider(skillData: SkillDetail): void {
    this.skillForm.patchValue({
      name: skillData.name || '',
      eqmsScopes: skillData.eQMSScopeIds || [],
      description: skillData.description || '',
      trainingCriteria: skillData.trainingCriteria || '',
      validityPeriodRange: skillData.validityPeriod?.range ?? null,
      validityPeriodInterval: skillData.validityPeriod?.interval ?? null,
      verificationRequired: !!skillData.verificationRequired,
      acknowledgementRequired: !!skillData.acknowledgementRequired,
      isActive: skillData.isActive ?? true
    });
  }

  /**
   * Builds update payload and calls update skill service.
   */
  UpdateSkill(): void {
    const formValue = this.skillForm.getRawValue();
    const validityPeriod: ValidityPeriod = {
      range: formValue.validityPeriodRange === '' ? null : formValue.validityPeriodRange,
      interval: formValue.validityPeriodInterval
    };

    const skillPayload: ISkillSaveRequest = {
      code: this.skillDetailsData?.code || '',
      name: formValue.name || '',
      eQMSScopes: (formValue.eqmsScopes || []).map((scopeId: number | string) => scopeId.toString()),
      description: formValue.description || '',
      trainingCriteria: formValue.trainingCriteria || '',
      validityPeriod,
      verificationRequired: !!formValue.verificationRequired,
      acknowledgementRequired: !!formValue.acknowledgementRequired,
      isActive: !!formValue.isActive
    };

    this.skillPersistenceService.UpdateSkillService(skillPayload);
  }

  private validateForm() {
    this.errorMessages = [];
    this.mandatoryFields = this.getDefaultMandatoryFieldsObject();
    if (this.skillForm.invalid) {
      Object.keys(this.skillForm.controls).forEach((key) => {
        if (this.skillForm.get(key)?.errors) {
          let errorText = this.getErrorMessageText(key);
          errorText && this.errorMessages.push(errorText);
        }
      });
    }
  }

    private getDefaultMandatoryFieldsObject() {
    return {
      name: false,
      eQMSScopes: false,
    };
  }

   private getErrorMessageText(labelName: string) {
      let errorText: string = '';
      switch (labelName) {
        case 'name':
          errorText = SkillConstants.Skill_Name_Required;
          this.mandatoryFields.name = true;
          break;
        case 'eqmsScopes':
          errorText = SkillConstants.Skill_eQMSScope_Required;
          this.mandatoryFields.eQMSScopes = true;
          break;
      }
      return errorText;
    }

    private validateValidityPeriod(){
      let validityPeriodInterval = this.skillForm.get('validityPeriodInterval')?.value;
      let validityPeriodRange = this.skillForm.get('validityPeriodRange')?.value;
      let verificationRequired = this.skillForm.get('verificationRequired')?.value;

      // Check if validity interval is selected but range is not entered
      if ((validityPeriodInterval !== null && validityPeriodInterval !== undefined) && 
          (validityPeriodRange === null || validityPeriodRange === '' || validityPeriodRange === undefined)) {
        this.errorMessages.push(SkillConstants.Skill_ValidityPeriod_must_Be_Greater_Than_Zero);
        return;
      }

      // Check if validity range is entered (not null, not empty, not zero or negative)
      if (validityPeriodRange !== null && validityPeriodRange !== '' && validityPeriodRange !== undefined) {
        // Check if validity period is greater than 0
        if (validityPeriodRange <= 0) {
          this.errorMessages.push(SkillConstants.Skill_ValidityPeriod_must_Be_Greater_Than_Zero);
          return;
        }

        // If range is > 0, interval must be selected
        if (validityPeriodInterval === null || validityPeriodInterval === undefined) {
          this.errorMessages.push(SkillConstants.Skill_ValidityPeriodInterval_Required_ValidityRangeIsEntered);
          return;
        }

        // If both range and interval are set, verification must be required
        if (verificationRequired === false) {
          this.errorMessages.push(SkillConstants.Skill_ValidityPeriod_VerificationRequired_ErrorMessage);
        }
      }
}

}
